import { defineStore } from 'pinia';
import { LanguageConfig } from '~/types';
import { Breadcrumb } from '~/types/page';
import { Facet, FacetData, FacetFilter, ProductHistory, Sequence } from '~/types/products';

const getDefaultProductHistory = (): ProductHistory => ({
  productId: null,
  route: undefined,
  variant: undefined,
});

export const useProductStore = defineStore('product', () => {
  const languageConfig = ref<LanguageConfig | object>({});
  const productHistory = ref<ProductHistory>(getDefaultProductHistory());
  const resultFacets = ref<Facet[]>([]);
  const sortSequence = ref<string[]>([]);
  const disabledFacets = ref<string[]>([]);

  const pushProductHistory = (payload: ProductHistory): void => {
    productHistory.value = { ...productHistory.value, ...payload };
  };

  const resetProductHistory = (): void => {
    productHistory.value = getDefaultProductHistory();
  };

  const clearFilters = (): void => {
    resultFacets.value = [];
  };

  const loadLanguageConfig = async (): Promise<void> => {
    if (!languageConfig.value) {
      const { $productService } = useNuxtApp();
      const { data, error } = await $productService.fetchLanguageConfig();
      const config = error ? {} : data!;
      languageConfig.value = config;
    }
  };

  const getSequencesByCategory = async (slug: string): Promise<void> => {
    sortSequence.value = [];
    const { $sequenceService, $sentry } = useNuxtApp();
    try {
      const { data } = (await $sequenceService.getSequencesByCategory(slug)) as { data: Sequence };
      sortSequence.value = data?.sequence || [];
      disabledFacets.value = data?.disabled || [];
    } catch (error) {
      $sentry.captureException(error);
    }
  };

  const getSequencesByBreadcrumb = async (breadcrumb: Breadcrumb): Promise<void> => {
    const {
      public: { brand },
    } = useRuntimeConfig();

    let categorySlug;

    // In dahl, categories deeper than 3 nodes are hidden from the product menu
    if (brand === 'dahl' && breadcrumb.parents) {
      categorySlug = breadcrumb.parents[2]?.slug ?? breadcrumb.slug;
    } else {
      categorySlug = breadcrumb.slug;
    }

    if (categorySlug) {
      await getSequencesByCategory(categorySlug.split('/').join('-'));
    }
  };

  const clearSequenceData = () => {
    sortSequence.value = [];
    disabledFacets.value = [];
  };

  const setFacetsFromSearchResult = ({ facets, facetsData }: { facets: Facet[]; facetsData: FacetData[] }): void => {
    if (facetsData) {
      const facetLabelMap: { [key: string]: string | undefined } = {}; // key = facet name / value = facet label
      facetsData.forEach((facet) => {
        const labelAttribute = facet.key?.attributes.find(
          // attribute.values[0] = facet value
          (attribute: { name: string; values: string[] }) =>
            attribute.name === 'FacetattributeLabel' && attribute.values.length > 0,
        );
        // facet.key.id = facet name
        facetLabelMap[facet.key!.id] = labelAttribute ? labelAttribute.values[0] : undefined;
      });
      // Add labels to facets
      facets.forEach((facet) => (facet.label = facetLabelMap[facet.name!]));
    }

    resultFacets.value = facets;
  };

  const appliedFilters = computed<FacetFilter[]>(() => {
    const appliedFilters: FacetFilter[] = [];

    const filteredResultFacets: Facet[] = resultFacets.value.filter(
      (facet) => !['filterTags', 'stockedAt'].includes(facet.name!),
    );

    filteredResultFacets.forEach((facet) => {
      const selectedValues = facet.items.filter((item: { selected: boolean }) => item.selected === true);
      if (selectedValues.length) {
        const value = {
          name: facet.name!,
          label: facet.label!,
          items: selectedValues,
        };

        appliedFilters.push(value);
      }
    });

    return appliedFilters;
  });

  return {
    languageConfig,
    productHistory,
    resultFacets,
    sortSequence,
    disabledFacets,
    appliedFilters,
    loadLanguageConfig,
    pushProductHistory,
    resetProductHistory,
    setFacetsFromSearchResult,
    clearFilters,
    getSequencesByCategory,
    clearSequenceData,
    getSequencesByBreadcrumb,
  };
});
